import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { paymentsService } from '../services/api';
import { format } from 'date-fns';
import { Banknote as BanknoteIcon, Hourglass as HourglassIcon } from 'lucide-react';
import { usePmsDataRefresh } from '../agent/dataSync';

function PaymentLog() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    method: 'all',
    dateFrom: '',
    dateTo: '',
  });

  useEffect(() => {
    loadPayments();
  }, []);

  // A payment recorded (or undone) by the PMS agent: reload the log in place.
  usePmsDataRefresh(['payments'], () => loadPayments({ silent: true }));

  // options.silent: reload without the loading state (used after agent changes)
  const loadPayments = async (options) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const result = await paymentsService.getAll();
      if (result && result.success && result.data) {
        setPayments(result.data);
      } else if (Array.isArray(result)) {
        setPayments(result);
      } else {
        setPayments([]);
      }
    } catch (error) {
      console.error('Error loading payments:', error);
      if (!silent) {
        toast.error('Failed to load payment log');
        setPayments([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'SUCCESS': { bg: '#E1E9E2', color: '#0F2619', label: 'Success' },
      'PENDING': { bg: '#F3E6CB', color: '#684A1F', label: 'Pending' },
      'FAILED': { bg: '#F2D9D3', color: '#5F241B', label: 'Failed' },
      'REFUNDED': { bg: '#E1E9E2', color: '#0F2619', label: 'Refunded' },
    };
    const style = statusColors[status] || { bg: '#EEEBE1', color: '#36433B', label: status };
    
    return (
      <span style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
      }}>
        {style.label}
      </span>
    );
  };

  const getMethodIcon = (method) => {
    const methodIcons = {
      'stripe': '',
      'razorpay': '',
      'cash': '',
      'bank_transfer': '',
      'upi': '',
      'credit_card': '',
      'debit_card': '',
    };
    return methodIcons[method] || '';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = !filters.search || 
      payment.booking?.id.toLowerCase().includes(filters.search.toLowerCase()) ||
      payment.booking?.user?.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      payment.booking?.user?.email.toLowerCase().includes(filters.search.toLowerCase()) ||
      payment.id.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesStatus = filters.status === 'all' || payment.status === filters.status;
    const matchesMethod = filters.method === 'all' || payment.method === filters.method;
    
    const paymentDate = new Date(payment.createdAt);
    const matchesDateFrom = !filters.dateFrom || paymentDate >= new Date(filters.dateFrom);
    const matchesDateTo = !filters.dateTo || paymentDate <= new Date(filters.dateTo + 'T23:59:59');
    
    return matchesSearch && matchesStatus && matchesMethod && matchesDateFrom && matchesDateTo;
  });

  const totalAmount = filteredPayments
    .filter(p => p.status === 'SUCCESS')
    .reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
        <h3>Loading Payment Log...</h3>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          Payment Log
          <button
            onClick={loadPayments}
            style={{
              marginLeft: 'auto',
              padding: '8px 16px',
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Refresh
          </button>
        </h2>
        
        {/* Summary Stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '15px', 
          marginBottom: '20px' 
        }}>
          <div style={{
            backgroundColor: '#F5F3EB',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#163626' }}>
              {formatCurrency(totalAmount)}
            </div>
            <div style={{ color: '#5F6B61', fontSize: '14px' }}>Total Successful Payments</div>
          </div>
          <div style={{
            backgroundColor: '#F5F3EB',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1A3E2A' }}>
              {filteredPayments.length}
            </div>
            <div style={{ color: '#5F6B61', fontSize: '14px' }}>Total Payments</div>
          </div>
          <div style={{
            backgroundColor: '#F5F3EB',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#163626' }}>
              {filteredPayments.filter(p => p.status === 'SUCCESS').length}
            </div>
            <div style={{ color: '#5F6B61', fontSize: '14px' }}>Successful</div>
          </div>
          <div style={{
            backgroundColor: '#F5F3EB',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8E3628' }}>
              {filteredPayments.filter(p => p.status === 'FAILED').length}
            </div>
            <div style={{ color: '#5F6B61', fontSize: '14px' }}>Failed</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '20px',
          padding: '20px',
          backgroundColor: '#F5F3EB',
          borderRadius: '8px',
          border: '1px solid #E3DFD3',
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
              Search
            </label>
            <input
              type="text"
              placeholder="Booking ID, User name, Email..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            >
              <option value="all">All Statuses</option>
              <option value="SUCCESS">Success</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
              Method
            </label>
            <select
              value={filters.method}
              onChange={(e) => setFilters(prev => ({ ...prev, method: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            >
              <option value="all">All Methods</option>
              <option value="stripe">Stripe</option>
              <option value="razorpay">Razorpay</option>
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="upi">UPI</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
              From Date
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
              To Date
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'end' }}>
            <button
              onClick={() => setFilters({
                search: '',
                status: 'all',
                method: 'all',
                dateFrom: '',
                dateTo: '',
              })}
              style={{
                padding: '8px 16px',
                backgroundColor: '#5F6B61',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                width: '100%',
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Payment Table */}
      {filteredPayments.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#F5F3EB',
          borderRadius: '8px',
          border: '1px solid #E3DFD3',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}><BanknoteIcon size="1em" aria-hidden="true" /></div>
          <h3 style={{ color: '#5F6B61', marginBottom: '10px' }}>No payments found</h3>
          <p style={{ color: '#A5A89B' }}>
            {payments.length === 0 ? 'No payments have been recorded yet.' : 'Try adjusting your filters.'}
          </p>
        </div>
      ) : (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          border: '1px solid #E3DFD3',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F5F3EB' }}>
                  <th style={headerStyle}>Payment ID</th>
                  <th style={headerStyle}>Date</th>
                  <th style={headerStyle}>Amount</th>
                  <th style={headerStyle}>Status</th>
                  <th style={headerStyle}>Method</th>
                  <th style={headerStyle}>Booking</th>
                  <th style={headerStyle}>Guest</th>
                  <th style={headerStyle}>Property</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment, index) => (
                  <tr key={payment.id} style={{ 
                    backgroundColor: index % 2 === 0 ? 'white' : '#F5F3EB',
                    borderBottom: '1px solid #E3DFD3' 
                  }}>
                    <td style={cellStyle}>
                      <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#5F6B61' }}>
                        {payment.id.slice(0, 8)}...
                      </div>
                      {payment.transactionId && (
                        <div style={{ fontSize: '11px', color: '#A5A89B', marginTop: '2px' }}>
                          TXN: {payment.transactionId.slice(0, 12)}...
                        </div>
                      )}
                    </td>
                    <td style={cellStyle}>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>
                        {formatDate(payment.createdAt)}
                      </div>
                    </td>
                    <td style={cellStyle}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#163626' }}>
                        {formatCurrency(payment.amount)}
                      </div>
                      {payment.baseAmount && payment.totalTaxAmount && (
                        <div style={{ fontSize: '11px', color: '#5F6B61', marginTop: '2px' }}>
                          Base: {formatCurrency(payment.baseAmount)} + Tax: {formatCurrency(payment.totalTaxAmount)}
                        </div>
                      )}
                    </td>
                    <td style={cellStyle}>
                      {getStatusBadge(payment.status)}
                    </td>
                    <td style={cellStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{getMethodIcon(payment.method)}</span>
                        <span style={{ textTransform: 'capitalize' }}>
                          {payment.method.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td style={cellStyle}>
                      {payment.booking ? (
                        <div>
                          <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#1A3E2A' }}>
                            {payment.booking.id.slice(0, 8)}...
                          </div>
                          <div style={{ fontSize: '11px', color: '#5F6B61', marginTop: '2px' }}>
                            {payment.booking.status}
                          </div>
                          {payment.booking.checkIn && payment.booking.checkOut && (
                            <div style={{ fontSize: '11px', color: '#A5A89B', marginTop: '2px' }}>
                              {format(new Date(payment.booking.checkIn), 'MMM dd')} - {format(new Date(payment.booking.checkOut), 'MMM dd')}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#A5A89B', fontSize: '12px' }}>No booking data</span>
                      )}
                    </td>
                    <td style={cellStyle}>
                      {payment.booking?.user ? (
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>
                            {payment.booking.user.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
                            {payment.booking.user.email}
                          </div>
                          {payment.booking.user.phone && (
                            <div style={{ fontSize: '11px', color: '#A5A89B', marginTop: '2px' }}>
                              {payment.booking.user.phone}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#A5A89B', fontSize: '12px' }}>No user data</span>
                      )}
                    </td>
                    <td style={cellStyle}>
                      {payment.booking?.room?.property ? (
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>
                            {payment.booking.room.property.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
                            Room: {payment.booking.room.name}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#A5A89B', fontSize: '12px' }}>No property data</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const headerStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontWeight: '600',
  fontSize: '14px',
  color: '#36433B',
  borderBottom: '1px solid #E3DFD3',
};

const cellStyle = {
  padding: '12px 16px',
  fontSize: '14px',
  verticalAlign: 'top',
};

export default PaymentLog;
